import {tokens} from '@commercial-helios/ui/theme'
import * as EmailValidator from 'email-validator'
import React, {useReducer} from 'react'
import styled from 'styled-components'

import Checkbox from '@helios/shared/components/checkbox'
import {SimpleSignupForm} from '@helios/shared/components/simple-signup-form'
import useNavigate from '@helios/shared/hooks/use-navigate'
import {useRecaptchaHubspotSubmit} from '@helios/shared/hooks/use-recaptcha-hubspot-submit'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {useLocale} from '@helios/shared/providers/locale'
import type {HubspotContactProps} from '@helios/shared/types/hubspot-contact-props'

interface Props {
    inputPlaceholder: string
    submitText: string
    emailInputErrorText: string
    consentErrorText: string
    hubspotProperties?: Partial<HubspotContactProps>
}

const initialState = {
    email: '',
    termsAccepted: false,
    loading: false,
    errorEmail: false,
    errorTermsAccepted: false,
    dirty: false,
}

type State = typeof initialState

enum ActionType {
    API_REQUEST_STARTED,
    API_REQUEST_FINISHED,
    API_REQUEST_FAILED,
    EMAIL_NOT_VALID,
    TERMS_NOT_ACCEPTED,
    EMAIL_ERROR_REMOVED,
    TERMS_ERROR_REMOVED,
    TERMS_CONSENT_CHANGED,
    EMAIL_UPDATED,
    FORM_TOUCHED,
}

type Action =
    | {type: ActionType.EMAIL_NOT_VALID}
    | {type: ActionType.TERMS_NOT_ACCEPTED}
    | {type: ActionType.API_REQUEST_STARTED}
    | {type: ActionType.API_REQUEST_FINISHED}
    | {type: ActionType.API_REQUEST_FAILED}
    | {type: ActionType.TERMS_CONSENT_CHANGED; value: boolean}
    | {type: ActionType.EMAIL_UPDATED; value: string}
    | {type: ActionType.EMAIL_ERROR_REMOVED}
    | {type: ActionType.TERMS_ERROR_REMOVED}
    | {type: ActionType.FORM_TOUCHED}

const reducer = (state: State, action: Action) => {
    switch (action.type) {
        case ActionType.EMAIL_NOT_VALID: {
            return {...state, errorEmail: true}
        }

        case ActionType.TERMS_NOT_ACCEPTED: {
            return {...state, errorTermsAccepted: true}
        }

        case ActionType.API_REQUEST_FAILED: {
            return {...state, errorEmail: true}
        }

        case ActionType.API_REQUEST_STARTED: {
            return {...state, loading: true}
        }

        case ActionType.API_REQUEST_FINISHED: {
            return {...state, loading: false}
        }

        case ActionType.EMAIL_ERROR_REMOVED: {
            return {...state, errorEmail: false}
        }

        case ActionType.TERMS_ERROR_REMOVED: {
            return {...state, errorTermsAccepted: false}
        }

        case ActionType.TERMS_CONSENT_CHANGED: {
            return {...state, termsAccepted: action.value}
        }

        case ActionType.EMAIL_UPDATED: {
            return {...state, email: action.value}
        }

        case ActionType.FORM_TOUCHED: {
            return {...state, dirty: true}
        }

        default: {
            throw new Error('Unrecognized action')
        }
    }
}

const FincircleForm = ({hubspotProperties, ...props}: Props) => {
    const [state, dispatch] = useReducer(reducer, initialState)
    const navigate = useNavigate()
    const currentLocale = useLocale()
    const {recaptchaHubspotSubmit} = useRecaptchaHubspotSubmit()
    const {recaptcha_and_privacy_terms} = useGlobalStrings()

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        if (!EmailValidator.validate(state.email)) {
            dispatch({type: ActionType.EMAIL_NOT_VALID})
            return
        }

        if (!state.termsAccepted) {
            dispatch({type: ActionType.TERMS_NOT_ACCEPTED})
            return
        }

        dispatch({type: ActionType.API_REQUEST_STARTED})
        await recaptchaHubspotSubmit(
            {
                email: state.email,
                country: currentLocale.country,
            },
            hubspotProperties,
            () => dispatch({type: ActionType.API_REQUEST_FAILED})
        )
        dispatch({type: ActionType.API_REQUEST_FINISHED})
        navigate('/thankyou-fincircle')
    }

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch({type: ActionType.EMAIL_ERROR_REMOVED})
        dispatch({type: ActionType.EMAIL_UPDATED, value: e.target.value})
        dispatch({type: ActionType.FORM_TOUCHED})
    }

    const handleTermsAcceptedChange = (checked: boolean) => {
        dispatch({type: ActionType.TERMS_ERROR_REMOVED})
        dispatch({type: ActionType.TERMS_CONSENT_CHANGED, value: checked})
    }

    return (
        <FormWrapper>
            <SimpleSignupForm
                handleSubmit={handleSubmit}
                handleEmailChange={handleEmailChange}
                email={state.email}
                loading={state.loading}
                error={state.errorEmail ? new Error() : null}
                errorText={props.emailInputErrorText}
                placeholder={props.inputPlaceholder}
                submitText={props.submitText}
                withBorder
            />
            {state.dirty && (
                <>
                    <Checkbox
                        label={recaptcha_and_privacy_terms}
                        checked={state.termsAccepted}
                        onChange={handleTermsAcceptedChange}
                        error={state.errorTermsAccepted ? props.consentErrorText : null}
                    />
                </>
            )}
        </FormWrapper>
    )
}

const FormWrapper = styled.div`
    display: grid;
    row-gap: ${tokens.spacing20};
    width: 100%;
    grid-template-columns: 1fr;
`

export default FincircleForm
